// Release notes shown in the "What's New" modal on app refresh after an update.
//
// Maintenance: when shipping a new release that's worth surfacing to users,
// add a new entry to the TOP of the array with a fresh `version` string.
// The modal compares the user's last-seen version (localStorage) to
// LATEST_VERSION below and surfaces every entry newer than what they've
// seen. New users (never seen any version) just get their last-seen value
// stamped silently — no dump-truck of historical notes.
//
// Version format is free-form but compared exactly, so bump it monotonically.

export interface ReleaseNote {
  version: string;
  date: string;     // ISO date for display
  title: string;    // headline shown at the top of that release's section
  summary?: string; // optional one-liner under the title
  items: { label: string; description?: string }[];
}

export const RELEASE_NOTES: ReleaseNote[] = [
  {
    version: "2026.05.06",
    date: "2026-05-06",
    title: "iOS-level polish, Bible Reader upgrade, and messaging facelift",
    summary: "The entire app now feels native. Smoother transitions, haptic feedback on key actions, and a completely redesigned scripture reading experience.",
    items: [
      {
        label: "Bible Reader overhaul",
        description: "Serif typography for scripture text, glass action sheet, chapter progress bar, skeleton loading, and haptic feedback on every interaction.",
      },
      {
        label: "Premium messaging",
        description: "Message bubbles now have iOS-style tails, gradient styling, glass typing indicator, and a refined input bar.",
      },
      {
        label: "Haptic feedback",
        description: "Tactile feedback on set completion, PR detection, workout finish, rest timer, check-in submit, message send, and session booking.",
      },
      {
        label: "Spring page transitions",
        description: "Every page now enters with a smooth spring animation instead of hard cuts. Feels like a native app.",
      },
      {
        label: "Faith Center redesign",
        description: "New gradient masthead, glass stat cards, horizontal scripture carousels, and a daily verse hero section.",
      },
      {
        label: "Skeleton loading upgrade",
        description: "Shimmer animations replace static loading states across every page.",
      },
    ],
  },
  {
    version: "2026.05.05",
    date: "2026-05-05",
    title: "Today page overhaul + design system upgrade",
    summary: "Your daily command center got a complete redesign. Every page now speaks the same visual language.",
    items: [
      {
        label: "Mission Briefing layout",
        description: "Today page rebuilt from the ground up. Faith-first scripture strip, daily pillar rings, training card with warmup/cooldown, and a full stats accordion.",
      },
      {
        label: "Per-category notification preferences",
        description: "Go to Settings → Notifications to turn on/off push alerts for wall posts, messages, weekly reports, and more.",
      },
      {
        label: "Blueprint nutrition fix",
        description: "Custom macro splits on mobile now save correctly. Auto-scale recalculates when you change the calorie target.",
      },
      {
        label: "Food log bottom sheet fix",
        description: "The food scanner sheet no longer gets clipped behind the bottom nav bar.",
      },
      {
        label: "Wall push notifications",
        description: "You now get a push when Coach posts on The Wall. No more missing updates.",
      },
      {
        label: "Architect design system",
        description: "Every page upgraded to Barlow Condensed headers, premium card styling, and consistent typography across the entire app.",
      },
    ],
  },
  {
    version: "2026.05.04",
    date: "2026-05-04",
    title: "Coach tools, sharing, and a tappable bell",
    summary: "A big batch. Coach can now manage client data in person, journals can be shared anywhere, and the notification bell finally takes you somewhere.",
    items: [
      {
        label: "Coach in-person logging",
        description: "Log a weigh-in or mark today's workout complete on a client's profile during a session. No need to hand them the phone.",
      },
      {
        label: "Coach can upload progress photos for any client",
        description: "Drop a front/back/side photo with optional weight. Photos uploaded by the coach get a small \"Coach\" badge.",
      },
      {
        label: "Profile editing polish",
        description: "Sex and training experience are now editable. Tap the avatar circle on a client profile to set their photo.",
      },
      {
        label: "Onboarding gate",
        description: "New clients are routed into the intake wizard on first login. Coach can reset onboarding from the client profile.",
      },
      {
        label: "Editable wall categories",
        description: "Settings → Wall Categories. Add up to 12 custom post categories with your own emoji + label, reorder them, restore defaults.",
      },
      {
        label: "Share journal entries",
        description: "Share to your wall, a community group, the device share sheet, copy link, email, or SMS.",
      },
      {
        label: "Devotional → Journal",
        description: "\"Open in Journal\" now carries the verse, reflection, application, and prayer into a structured entry. Your response goes at the bottom.",
      },
      {
        label: "Notifications take you somewhere",
        description: "Tap any row in the bell to jump to its target. Daily check-in, habits, schedule, follower profile, etc.",
      },
      {
        label: "Rich text journal editor",
        description: "Format entries with fonts, colors, lists, alignment, blockquote, links, and clear-formatting.",
      },
      {
        label: "PWA install prompt redesign",
        description: "Hero card with benefits on Android; step-by-step Safari guide on iOS that highlights the actual Share button.",
      },
    ],
  },
];

export const LATEST_VERSION = RELEASE_NOTES[0]?.version ?? "0";

export function notesNewerThan(lastSeen: string | null): ReleaseNote[] {
  if (!lastSeen) return [];
  // Treat anything that doesn't match the latest as "newer", and cap at the
  // last 5 releases so a long-dormant user doesn't get a wall of changelog.
  // Exact-match short-circuits to avoid showing the same notes again.
  if (lastSeen === LATEST_VERSION) return [];
  const idx = RELEASE_NOTES.findIndex((r) => r.version === lastSeen);
  if (idx === -1) {
    // Last-seen string isn't in our list (older than what we've kept around)
    // — show the latest 5 entries.
    return RELEASE_NOTES.slice(0, 5);
  }
  // idx > 0 means the user has seen up through that index; show everything
  // before it.
  return RELEASE_NOTES.slice(0, idx);
}
